import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRedoAlt,
  faUndoAlt,
  faExpand,
  faDownload,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import "./ImageViewerModal.css";

export default function ImageViewerModal({
  show,
  onHide,
  image,
  photos,
  photoIndex,
  onRotate,
  setSelectedPhoto,
}) {
  const [rotation, setRotation] = useState(image?.rotation || 0);
  const [loading, setLoading] = useState(false);
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const dialogRef = useRef(null);

  const normalizeRotation = (deg) => ((deg % 360) + 360) % 360;

  const handleRotate = (direction) => {
    const newRotation = direction === "right" ? rotation + 90 : rotation - 90;
    const normalized = normalizeRotation(newRotation);
    setRotation(normalized);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onRotate(image, rotation);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const handleClose = () => {
    setRotation(0);
    setSelectedPhoto(null);
    onHide();
  };

  const handleImageClick = () => {
    const viewerUrl = `/photo-viewer?photos=${encodeURIComponent(
      JSON.stringify(photos)
    )}&photo=${photoIndex}`;
    window.open(viewerUrl, "_blank");
  };

  useEffect(() => {
    setRotation(image?.rotation || 0);
  }, [image]);

  useEffect(() => {
    if (show && dialogRef.current) {
      dialogRef.current.showModal();
    } else if (dialogRef.current) {
      dialogRef.current.close();
    }
  }, [show]);

  useEffect(() => {
    const adjustImageSize = () => {
      const img = imgRef.current;
      const container = containerRef.current;

      if (img && container) {
        // Reset any previous sizing
        img.style.width = "";
        img.style.height = "";

        // Get current dimensions
        let width = img.naturalWidth;
        let height = img.naturalHeight;

        // Swap dimensions if rotated 90 or 270 degrees
        const isRotated = rotation === 90 || rotation === 270;
        if (isRotated) {
          [width, height] = [height, width];
        }

        // Set maximum dimensions
        const MAX_WIDTH = 600;
        const MAX_HEIGHT = 510;

        // Calculate available space (with padding and respecting max dimensions)
        const containerWidth = Math.min(container.clientWidth - 20, MAX_WIDTH);
        const containerHeight = Math.min(
          container.clientHeight - 20,
          MAX_HEIGHT
        );

        // Calculate scale factors for both dimensions
        const widthRatio = containerWidth / width;
        const heightRatio = containerHeight / height;

        // Determine which dimension to prioritize
        let scale;
        if (height > containerHeight || height < containerHeight) {
          // If image is taller than container, fit to height
          scale = heightRatio;
        } else if (width > containerWidth) {
          // If image is wider than container, fit to width
          scale = widthRatio;
        } else {
          // If image is smaller than container, don't scale up
          scale = 1;
        }

        let finalWidth;
        let finalHeight;

        // Apply scaling while maintaining aspect ratio
        const scaledWidth = width * scale;
        const scaledHeight = height * scale;

        if (rotation === 90 || rotation === 270) {
          finalWidth = Math.min(500, containerWidth);
          finalHeight = Math.min(490, containerHeight);
        } else {
          finalWidth = Math.min(scaledWidth, containerWidth);
          finalHeight = Math.min(scaledHeight, containerHeight);
        }
        img.style.width = `${finalWidth}px`;
        img.style.height = `${finalHeight}px`;
      }
    };

    // Run immediately if image is already loaded
    if (imgRef.current?.complete) {
      adjustImageSize();
    }

    // Set up load and resize listeners
    const img = imgRef.current;
    if (img) {
      img.onload = adjustImageSize;
    }

    window.addEventListener("resize", adjustImageSize);

    return () => {
      if (img) {
        img.onload = null;
      }
      window.removeEventListener("resize", adjustImageSize);
    };
  }, [rotation, image?.url]);

  return (
    <dialog
      ref={dialogRef}
      className="image-viewer-dialog"
      onClose={handleClose}
    >
      <div className="dialog-header">
        <h5 className="dialog-title">Image Viewer</h5>
        <div style={{ display: "flex" }}>
          <div className="toolbar">
            <button
              className="toolbar-btn"
              onClick={() => handleRotate("left")}
            >
              <FontAwesomeIcon icon={faUndoAlt} />
            </button>
            <button
              className="toolbar-btn"
              onClick={() => handleRotate("right")}
            >
              <FontAwesomeIcon icon={faRedoAlt} />
            </button>
            <button className="toolbar-btn" onClick={handleImageClick}>
              <FontAwesomeIcon icon={faExpand} />
            </button>
            <button
              className="toolbar-btn btn btn-primary"
              onClick={handleSave}
            >
              {loading ? (
                <Spinner animation="border" variant="success" size="sm" />
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} /> Save
                </>
              )}
            </button>
          </div>
          <button className="close-btn" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      <div
        className="image-container"
        ref={containerRef}
        style={{ overflow: "hidden" }}
      >
        <img
          ref={imgRef}
          src={image?.url}
          alt="Full size"
          className="modal-image"
          onLoad={() => {
            if (!image?.rotation) {
              setRotation(0);
            }
          }}
          style={{
            transform: `rotate(${rotation}deg)`,
          }}
        />
      </div>
    </dialog>
  );
}
